import { useEffect, useState } from 'react'
import { MessageCircle } from 'lucide-react'
import { peekResolverCache, resolveOrganization } from 'featurebase-js'
import { useFeaturebase } from 'featurebase-js/react'
import { useUnreadChangelog } from '../../hooks/useUnreadChangelog'
import { useIsFeaturebaseAvailable } from '../../lib/featurebase-context'
import { formatBadgeCount } from '../../utils/badgeCount'

/**
 * Header icon that opens the Featurebase messenger (chat + help articles).
 *
 * A red badge counts unread chat replies plus unread updates (changelog).
 * Tapping opens on Messages for an unread reply, else on Updates for an
 * unread update (marking updates read), else on Home. It never opens by
 * itself — the count is the only prompt.
 *
 * Featurebase's own floating chat bubble is hidden in
 * ConditionalFeaturebaseProvider, so this icon is the only way in. Renders
 * nothing where Featurebase is switched off, so the SDK is never touched
 * there.
 *
 * The messenger only boots when the workspace has Support switched on
 * (Featurebase dashboard → Settings → General → Manage products), so the
 * icon only shows then. Featurebase caches that setting per browser for
 * ~20 minutes, so a dashboard change can take that long to show here.
 */
export function MessagesButton() {
  const isFeaturebaseAvailable = useIsFeaturebaseAvailable()
  if (!isFeaturebaseAvailable) {
    return null
  }
  return <MessagesButtonInner />
}

/**
 * Whether the workspace has Support switched on. Reads Featurebase's cached
 * workspace settings first (the Provider has usually fetched them already),
 * then confirms with the resolver. False while unknown or if the lookup fails.
 */
function useIsSupportEnabled(): boolean {
  const appId = import.meta.env.VITE_FEATUREBASE_APP_ID
  const [isSupportEnabled, setIsSupportEnabled] = useState(
    () => peekResolverCache(appId)?.modules.support ?? false,
  )

  useEffect(() => {
    let isCancelled = false
    resolveOrganization(appId)
      .then((organization) => {
        if (!isCancelled) {
          setIsSupportEnabled(organization.modules.support)
        }
      })
      .catch(() => {
        // Lookup failed — keep the icon hidden rather than show a dead button.
      })
    return () => {
      isCancelled = true
    }
  }, [appId])

  return isSupportEnabled
}

function MessagesButtonInner() {
  const isSupportEnabled = useIsSupportEnabled()
  if (!isSupportEnabled) {
    return null
  }
  return <MessagesButtonIcon />
}

function MessagesButtonIcon() {
  const { show, showMessages, showSpace, unreadCount } = useFeaturebase()
  const { hasUnreadUpdates, unreadUpdatesCount, markUpdatesRead } =
    useUnreadChangelog()
  const hasUnreadMessages = unreadCount > 0
  const totalUnread = unreadCount + unreadUpdatesCount
  const badgeLabel = formatBadgeCount(totalUnread)

  // Open on whatever the count is for. A support reply wins over an update;
  // the update keeps the count showing so the next tap opens Updates.
  const handleClick = () => {
    if (hasUnreadMessages) {
      showMessages()
      return
    }
    if (hasUnreadUpdates) {
      markUpdatesRead()
      showSpace('changelog')
      return
    }
    show()
  }

  const ariaLabel =
    totalUnread > 0 ? `Messages, ${badgeLabel} unread` : 'Messages'

  return (
    <button
      type="button"
      onClick={handleClick}
      // 56px floating circle in the header colour with a thin, soft light
      // ring and shadow, so it stands out where it overlaps the page below.
      className="relative inline-flex items-center justify-center w-14 h-14 rounded-full border-2 shadow-lg cursor-pointer transition-transform active:scale-[0.98] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
      style={{
        backgroundColor: 'var(--nav-bg)',
        color: 'var(--nav-text)',
        borderColor: 'rgba(255, 255, 255, 0.6)',
      }}
      aria-label={ariaLabel}
    >
      {/* Filled speech bubble with a "?" cut in the header colour. Sized to
          fill most of the circle so it doesn't get lost (9-Oct-26 call). */}
      <span className="relative inline-flex items-center justify-center" aria-hidden="true">
        <MessageCircle className="w-9 h-9" fill="currentColor" />
        <span
          className="absolute text-base font-bold leading-none"
          style={{ color: 'var(--nav-bg)' }}
        >
          ?
        </span>
      </span>
      {totalUnread > 0 && (
        <span
          // Unread count on the circle's top-right edge, with the same thin
          // soft outline as the circle. red-600 keeps the small white text
          // above the 4.5:1 contrast minimum (red-500 is ~3.8:1).
          className="absolute -top-1 -right-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold leading-none"
          style={{ boxShadow: '0 0 0 1px rgba(255, 255, 255, 0.6)' }}
          aria-hidden="true"
        >
          {badgeLabel}
        </span>
      )}
    </button>
  )
}
